import { FC } from 'react';
import { BleConnectableDeviceListItem } from 'src/redux/ble/selectors';
import { ControllerCard } from '../controller-card/ControllerCard';
import { IonRow, IonSpinner } from '@ionic/react';
import './ConnectControllerDetails.scss';
import { BleControllerDevice } from 'src/redux/ble/types';
import { DqText } from '../dq-text';
import { useIntl } from 'react-intl';
import { ControllerMessages } from 'src/intl/ControllerMessages';

interface ConnectedDevice extends BleControllerDevice {
  description: string | undefined;
  physicalSerial: string | undefined;
  isControllerInOrg: boolean;
}

interface ConnectControllerDetailsProps {
  /**
   * The device connected to the controller
   */
  connectedDevice?: ConnectedDevice | undefined;
  /**
   * The list of connectable devices
   */
  connectableDevices: BleConnectableDeviceListItem[];
  /**
   * Whether the list is loading
   */
  isLoading: boolean;
  /**
   * Whether the controller is connecting
   */
  isControllerConnecting: boolean;
  /**
   * The callback triggered when a card is clicked
   */
  onCardClick: (controller: BleControllerDevice) => void;
}

const formatFirmwareVersion = (version: string | undefined) => {
  return version?.replace(/ctrl app v/gi, '');
};

export const ConnectControllerDetails: FC<ConnectControllerDetailsProps> = ({
  connectedDevice,
  connectableDevices,
  isLoading,
  isControllerConnecting,
  onCardClick,
}) => {
  const { formatMessage } = useIntl();

  const connectedControllerInfo = formatMessage(ControllerMessages.connectedControllerCardDescription, {
    physicalId: connectedDevice?.physicalSerial,
  });

  return (
    <div>
      <div className="connection-progress-container">
        {isLoading ? (
          <div className="spinner-container">
            <IonRow className="spinner-row">
              <IonSpinner name="circular" />
            </IonRow>
            <IonRow className="ion-margin-top">
              {isControllerConnecting ? (
                <DqText>{formatMessage(ControllerMessages.connecting)}.</DqText>
              ) : (
                <DqText className="spinner-row">{formatMessage(ControllerMessages.scanning)}</DqText>
              )}
            </IonRow>
          </div>
        ) : (
          !connectedDevice && <DqText variant="body-strong">{formatMessage(ControllerMessages.scanComplete)}</DqText>
        )}
      </div>
      {connectedDevice && (
        <div className="connected-card-container">
          <DqText>{connectedControllerInfo} </DqText>
        </div>
      )}
      {connectableDevices
        .filter(e => e.deviceId !== connectedDevice?.deviceId) // Remove the connected device from the list
        .map(bleDevice => {
          return (
            <div className="connectable-devices" key={bleDevice.deviceId}>
              <ControllerCard
                key={bleDevice.deviceId}
                physicalSerialNumber={bleDevice.physicalSerial}
                firmwareDisplayName={formatFirmwareVersion(bleDevice.firmwareVersion)}
                onClick={() => onCardClick(bleDevice)}
                disabled={isControllerConnecting}
              />
            </div>
          );
        })}
    </div>
  );
};
